import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GitHubRepository,
  EnvVarInput,
  RepositoryBuildInfo,
  RepositoryPathStatus,
  RunAssistance,
} from "@back4app2/sdk/lib/Back4app2";
import { ContainerType, ENV_VAR_FORM } from "../types/containerTypes";

import styles from "../assets/styles/CreateContainerForm.module.css";

import ToggleSwitchCheckbox from "../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox";
import TextInput from "../components/FormInputField/TextInput/TextInput";

import { ReactComponent as PlusIcon } from "../assets/images/plus-icon.svg";
import { ReactComponent as ErrorIcon } from "../assets/images/error-icon.svg";
import { ReactComponent as TrashIcon } from "../assets/images/trash-icon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/images/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../assets/images/visibility-off-icon.svg";
import { ReactComponent as FolderIcon } from "../assets/images/folder-icon.svg";
import { ReactComponent as TriangleExclamationIcon } from "../assets/images/triangle-exclamation.svg";

import NoAppDetectedNotice from "../components/BuildMethodSelector/NoAppDetectedNotice";
import Skeleton from "../components/Skeleton";
import {
  BuildpackLayersIcon,
  DOCKER_LOGO,
  getStackLogo,
} from "../components/BuildMethodSelector/stackLogos";
import RuntimeVersionField, {
  isValidRuntimePin,
} from "../components/BuildMethodSelector/RuntimeVersionField";
import BuildMethodSelector, {
  BuildMethod,
} from "../components/BuildMethodSelector/BuildMethodSelector";
import RepositoryBrowser from "../components/RepositoryBrowser/RepositoryBrowser";
import BranchSelect from "../components/BranchSelect/BranchSelect";
import { pushGTMEvent } from "../utils/gtm";
import back4app2 from "../back4app2";
import { DsButton, InfoTip } from '../components/ds';
import { DatabaseIcon } from '../components/ds/icons';
import useIsMobile from '../hooks/useIsMobile';
import { HEALTH_CHECK_TIP, PORT_TIP, ROOT_DIRECTORY_TIP, START_COMMAND_TIP, startCommandHelp } from '../utils/fieldHelp';

interface ContainerFormProps {
  isCreatingApp: boolean;
  creatingAppErrorMessage: string;
  onCancel: () => void;
  onSubmit: (container: ContainerType) => void;
  selectedRepository: GitHubRepository | undefined;
  /**
   * The branch and folder picked on the source step, beside the repository.
   *
   * Optional because Settings mounts this form with no source step in front
   * of it; there the repository's own default branch is the seed, as before.
   */
  initialBranchName?: string;
  initialRootDirectoryPath?: string;
  // Where the app's code comes from. On the image route the Source and Build
  // Method sections have nothing to configure — the artifact is already built
  // — so they are hidden rather than shown empty. Everything else on this
  // form (name, port, env vars, health check) is the same either way, which
  // is why this is a prop and not a second copy of the file.
  source?: 'GITHUB' | 'DOCKER_HUB';
  /**
   * The names of the apps this account already owns.
   *
   * `apps_user_id_namex` is UNIQUE on (user_id, name) for live apps, and the
   * worker's retry loop only recovers from a slug clash — a name clash is
   * rethrown and comes back as "You already have an app with this same name"
   * after the create task has run. Now that the name arrives pre-filled from
   * the repository, that collision is ours to avoid rather than the user's to
   * make: deploying the same repository twice is an ordinary thing to want.
   *
   * Empty until the list loads, and a stale list is possible either way, so
   * this only makes the common case pleasant — the server keeps the guarantee.
   */
  takenNames?: string[];
  /**
   * What the image route is deploying, as `repository:tag`.
   *
   * Read-only here: the image is chosen on the step before this one, and the
   * way to change it is to go back. It is on the review anyway because a card
   * that says only "Name" does not tell someone what they are about to create.
   */
  imageReference?: string;
  /**
   * The app plan and the running total — the rail beside the review.
   *
   * It is a slot rather than a section of this file because the plan is the
   * flow's state, not the form's. The form wraps it in the rail card and
   * appends the Create button to the bottom of that same card: the price and
   * the button that charges it are then one object, visible from the first
   * screen, and there is nothing left to scroll to before deciding.
   */
  rail?: React.ReactNode;
  /**
   * What is sold on top of the instance, as the last card of the review
   * column. Not in the rail: adding a database is a decision about the app,
   * and the rail is only what it costs.
   */
  extras?: React.ReactNode;
  /** The line under the Create button — what will be charged, and when. */
  footerTitle?: React.ReactNode;
  footerSubtitle?: React.ReactNode;
  submitLabel?: string;
  /**
   * Variables Back4app will set on this app, by name.
   *
   * Only the names: the values do not exist yet — the database is provisioned
   * on the first deploy — and would not be the customer's to type in if they
   * did. Shown anyway, because the alternative is an app that boots with a
   * `DATABASE_URL` nobody on this screen was told about, and a customer who
   * adds their own and watches it stop working.
   */
  managedEnvVarKeys?: string[];
  /**
   * The variable the bought database will be handed to the app through, as it
   * currently stands — the default, the name the analysis read out of the
   * code, or whatever has been typed over it.
   *
   * Reported upward because the extras card says it, and the card is built a
   * component above this one while the name is decided here: it depends on the
   * analysis, which only this component runs.
   */
  onManagedDatabaseKeyChange?: (key: string) => void;
  /** The name typed for the app, so the rail's first line can carry it. */
  onAppNameChange?: (name: string) => void;
  /**
   * Adds the database extra — and, on a plan that does not sell it, moves to
   * the cheapest one that does, as the Add button in Features already does.
   * Offered next to a connection string the analysis found the project needs.
   * Absent when the catalog sells no database; then only "use your own" is said.
   */
  onAddDatabase?: () => void;
  /** What that database costs a month, said next to the offer to add it. */
  databasePrice?: number;
  /**
   * Whether the plan chosen on this screen includes auto deploy.
   *
   * A prop rather than something this form works out: the plan is picked in
   * the block the flow hands us as `children`, so the form has no way to read
   * it — and the toggle was hard-disabled for everyone because of that,
   * including a buyer who had just chosen a paid plan two fields below it.
   *
   * Free is the default. A form with no plan concept at all — the image route
   * reuses this file — keeps the behaviour it always had.
   */
  autoDeployAvailable?: boolean;
  /**
   * Turning auto deploy on while the plan does not include it: the flow picks
   * the cheapest paid plan (`paidPlanName`) instead of the toggle refusing.
   * Absent, the toggle stays locked, as it always was.
   */
  onRequirePaidPlan?: () => void;
  paidPlanName?: string;
  /** Green is for the free path; blue for one that charges. */
  submitVariant?: 'primary' | 'action';
  isSubmitDisabled?: boolean;
}
interface ContainerFormType {
  containerName: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  branchName: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  rootDirectoryPath: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  autoDeploy: {
    value: boolean;
  };
  environmentVars: ENV_VAR_FORM[];
  exposedPort: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  healthCheckEndpoint: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  runtimeVersion: {
    value: string;
  };
  buildMethod: {
    value: BuildMethod;
  };
  startCommand: {
    value: string;
  };
}

const INITIAL_STATE: ContainerFormType = {
  containerName: {
    value: "",
    isValid: true,
    isRequired: true,
    errorMsg: "",
  },
  branchName: {
    value: "",
    isValid: true,
    isRequired: true,
    errorMsg: "",
  },
  rootDirectoryPath: {
    value: "./",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  autoDeploy: { value: false },
  environmentVars: [],
  exposedPort: {
    value: "",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  healthCheckEndpoint: {
    value: "/",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  buildMethod: { value: "DOCKERFILE" },
  runtimeVersion: { value: "" },
  startCommand: { value: "" },
};

/**
 * `base`, or the first "base-2", "base-3" … that this account does not already
 * have. Terminates: `taken` is finite.
 */
/** The name the managed connection string goes out under by default. */
const DEFAULT_DATABASE_URL_ENV_KEY = "DATABASE_URL";

/**
 * A variable that holds a Postgres connection string, by the names projects
 * actually use: DATABASE_URL, DB_URL, POSTGRES_URL, PG_CONNECTION_STRING,
 * SUPABASE_DB_URL. Read off the NAME, not the analysis' prose — a name is what
 * the code reads, and what the managed database will be delivered under.
 */
const DATABASE_URL_KEY_PATTERN =
  /^(?:[A-Z0-9]+_)*(?:DATABASE|DB|POSTGRES|POSTGRESQL|PG)_(?:URL|URI|DSN|CONNECTION|CONNECTION_STRING|CONN_STRING)$/i;

const availableName = (base: string, taken: string[]): string => {
  if (!base || !taken.includes(base)) {
    return base;
  }

  for (let i = 2; ; i++) {
    const candidate = `${base}-${i}`;

    if (!taken.includes(candidate)) {
      return candidate;
    }
  }
};

const CreateContainerForm = ({
  isCreatingApp,
  creatingAppErrorMessage,
  onCancel,
  onSubmit,
  selectedRepository,
  initialBranchName,
  initialRootDirectoryPath,
  source = 'GITHUB',
  takenNames = [],
  imageReference,
  rail,
  extras,
  footerTitle,
  footerSubtitle,
  submitLabel = 'Create app',
  autoDeployAvailable = false,
  onRequirePaidPlan,
  paidPlanName,
  managedEnvVarKeys = [],
  onManagedDatabaseKeyChange,
  onAppNameChange,
  onAddDatabase,
  databasePrice,
  submitVariant = 'action',
  isSubmitDisabled = false,
}: ContainerFormProps) => {
  const isImageSource = source === 'DOCKER_HUB';
  const [container, setContainer] = useState<ContainerFormType>({
    ...INITIAL_STATE,
    // Named after the repository it comes from, like the branch below it.
    // Everything else on this step arrives filled in from the detection, and
    // the one empty required field was the one thing nobody could guess for
    // us — except that they had just picked the name, one screen ago.
    // Still editable: it is a default, not a decision.
    containerName: {
      ...INITIAL_STATE.containerName,
      value: availableName(
        selectedRepository?.fullName.split('/').pop() || "",
        takenNames
      ),
    },
    // Both arrive from the source step, where they are now asked beside the
    // repository itself — so the detection's first attempt is at the branch
    // and the folder the user actually meant. The repository's default branch
    // is the fallback for a caller that does not ask (Settings).
    branchName: {
      ...INITIAL_STATE.branchName,
      value: initialBranchName || selectedRepository?.defaultBranch || "",
    },
    rootDirectoryPath: {
      ...INITIAL_STATE.rootDirectoryPath,
      value: initialRootDirectoryPath || INITIAL_STATE.rootDirectoryPath.value,
    },
  });
  const nameRef = useRef<HTMLInputElement>(null);
  const isNarrowScreen = useIsMobile();
  // A default is ours to keep correct; a typed name is not ours to touch.
  const userEditedNameRef = useRef(false);

  // The list can land after this form has already mounted — the user reaches
  // the step by clicking Select, and nothing waits for the query. While the
  // name is still the one we suggested, re-suggest it against what we now
  // know; once it has been typed in, it is the user's and stays put.
  useEffect(() => {
    if (userEditedNameRef.current || !takenNames.length) {
      return;
    }

    const base = selectedRepository?.fullName.split('/').pop() || "";

    if (!base) {
      return;
    }

    setContainer((prev) => ({
      ...prev,
      containerName: {
        ...prev.containerName,
        value: availableName(base, takenNames),
        isValid: true,
        errorMsg: "",
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takenNames, selectedRepository]);

  // What the repository has at the current branch + root dir. The detection
  // pre-selects the Build Method; it does not decide it — whatever ends up
  // selected is always submitted explicitly.
  //
  // The last answer and "a detection is running" are separate state on
  // purpose: re-detecting (typing a path, switching branch) keeps the previous
  // answer on screen instead of blanking the section on every keystroke.
  const [buildInfo, setBuildInfo] = useState<RepositoryBuildInfo | null>(null);
  const [isDetecting, setIsDetecting] = useState(true);
  /**
   * What the code analyzer understood about this repository.
   *
   * It used to be read only AFTER the app existed — the creation task analysed
   * the repository and the wizard offered the result on a screen of its own.
   * That screen is gone, and the paid route never reached it anyway: a paid
   * app is created pending payment, which is precisely the branch that skips
   * the gate. So the answer is asked for HERE, while the form is still open
   * and everything it suggests is still a field somebody can edit.
   *
   * Undefined is the ordinary case, not an error: the analyzer can be off, the
   * branch can be empty, the read can fail, and a repository judged malicious
   * deliberately returns the same nothing as all of those. The form works
   * exactly as it did before this existed whenever it does.
   */
  const [runAssistance, setRunAssistance] = useState<RunAssistance | undefined>(
    undefined
  );
  // Separate from `isDetecting`: the stack detection is one GitHub listing and
  // comes back in a moment, the analysis can take tens of seconds. Sharing one
  // flag would hold the Build Method section dim for the whole analysis.
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // The name typed for the managed database's connection string. Unused until
  // someone types — before that the name follows what the analysis found.
  const [databaseKeyInput, setDatabaseKeyInput] = useState("");
  const userEditedDatabaseKeyRef = useRef(false);
  /**
   * The variable names WE put in the list.
   *
   * Kept so that switching branch or folder replaces the previous suggestions
   * instead of stacking a second set on top of them — and so that a row the
   * user has already typed a value into is never one of the ones taken away.
   */
  const suggestedEnvKeysRef = useRef<string[]>([]);
  const [isBrowsingRepository, setIsBrowsingRepository] = useState(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  // Only a list we actually received licenses us to call a typed name wrong.
  // When the listing fails we know nothing about which branches exist, and
  // rejecting the name would block an app over our own outage.
  const [hasBranchList, setHasBranchList] = useState(false);
  // Ignores the answer to a request that is no longer the current one: the
  // repository can change, and Refresh can be pressed twice.
  const branchesRequestRef = useRef(0);

  // Branch names for the picker. A failure here is not fatal: the field is a
  // combobox, so an empty list simply leaves it as free text.
  const loadBranches = useCallback(() => {
    if (!selectedRepository) {
      return;
    }

    const requestId = branchesRequestRef.current + 1;
    branchesRequestRef.current = requestId;

    setIsLoadingBranches(true);

    back4app2
      .repositoryBranches(selectedRepository.id)
      .then((result) => {
        if (branchesRequestRef.current === requestId) {
          setBranches(result);
          setHasBranchList(true);
        }
      })
      .catch((e) => {
        console.error("error listing repository branches", e);

        if (branchesRequestRef.current === requestId) {
          setBranches([]);
          setHasBranchList(false);
        }
      })
      .finally(() => {
        if (branchesRequestRef.current === requestId) {
          setIsLoadingBranches(false);
        }
      });
  }, [selectedRepository]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);
  // Detection failed (bad branch, network): fail open to the classic behavior
  // instead of blocking, but the browser has nothing to show.
  const hasDockerfile = buildInfo ? buildInfo.hasDockerfile : null;
  // A path that doesn't exist on this branch (or points at a file) says
  // nothing about Dockerfiles or stacks. The browser reports that instead —
  // locking the Build Method on it would be a lie.
  const isRealDirectory =
    buildInfo?.pathStatus === RepositoryPathStatus.DIRECTORY;
  // Environment-level kill switch: while the route is off, the form behaves
  // exactly as it did before it existed. Fail-closed — an unanswered detection
  // must not advertise a route that would then refuse to build.
  const buildpacksAvailable = !!buildInfo?.buildpackBuildsEnabled;
  // Only meaningful on the buildpack path — a Dockerfile decides its own
  // serving.
  const isStaticSite =
    container.buildMethod.value === "BUILDPACK" && !!buildInfo?.isStaticSite;
  // The folder exists but holds nothing we can deploy. Drives the warning, the
  // card subtitle and hiding the Start Command — three places that would
  // otherwise contradict each other.
  /**
   * The command the analyzer would run, when it is not already what the field
   * says. Undefined covers every ordinary case: no analysis, nothing to
   * suggest, or a field that already agrees with it.
   */
  const analyzerStartCommand =
    runAssistance?.startCommand &&
    runAssistance.startCommand !== container.startCommand.value
      ? runAssistance.startCommand
      : undefined;

  const nothingDetected =
    !isDetecting &&
    isRealDirectory &&
    !!buildInfo &&
    !buildInfo.hasDockerfile &&
    !buildInfo.detectedStack;

  // The variables the analysis says this project cannot start without.
  // `required: false` is a conditional need: offered, never pointed at.
  const requiredEnvKeys = (runAssistance?.requiredEnvVars || [])
    .filter((envVar) => envVar && envVar.key && envVar.required !== false)
    .map((envVar) => envVar.key);

  // The one of them that is a database connection string, if any. That one is
  // a choice rather than a blank to fill: Back4app can provide the database, or
  // the customer brings their own.
  const databaseSuggestionKey = requiredEnvKeys.find((key) =>
    DATABASE_URL_KEY_PATTERN.test(key)
  );

  const hasManagedDatabase = managedEnvVarKeys.length > 0;

  // The name the managed connection string goes out under. Follows what the
  // code reads until the customer types one: a project that reads POSTGRES_URL
  // should receive POSTGRES_URL without anyone having to notice.
  const managedDatabaseKey = userEditedDatabaseKeyRef.current
    ? databaseKeyInput
    : databaseSuggestionKey || DEFAULT_DATABASE_URL_ENV_KEY;

  useEffect(() => {
    onManagedDatabaseKeyChange?.(managedDatabaseKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedDatabaseKey]);

  useEffect(() => {
    onAppNameChange?.(container.containerName.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container.containerName.value]);

  // With the database bought, the suggested row IS the managed one: showing
  // both asked the customer to type a connection string Back4app was about to
  // provide — and a typed one would have won at launch.
  const isCoveredByManagedDatabase = (envVar: ENV_VAR_FORM) =>
    hasManagedDatabase &&
    (envVar.name.value === managedDatabaseKey ||
      (envVar.name.value === databaseSuggestionKey &&
        envVar.value.value === ""));

  const visibleEnvVars = container.environmentVars.filter(
    (envVar) => !isCoveredByManagedDatabase(envVar)
  );

  // Found in the code and still empty. Pointed out, never held against the
  // submit: the analysis can be wrong, a value can be added later in Settings,
  // and the person creating the app is the one who knows.
  const missingRequiredEnvIds = visibleEnvVars
    .filter(
      (envVar) =>
        requiredEnvKeys.includes(envVar.name.value) &&
        envVar.value.value.trim() === ""
    )
    .map((envVar) => envVar.id);

  // Exactly what `handleFormSubmit` refuses to submit on. Kept apart from the
  // count below because the footer used to call all of it "you can continue
  // anyway" — true of a stack we failed to detect, false of a name this
  // account already uses, which returns early and creates nothing. One line
  // saying both is the line nobody believes.
  const blockingCount = [
    container.containerName.isValid === false,
    !isImageSource && container.branchName.isValid === false,
    !!container.runtimeVersion.value &&
      !isValidRuntimePin(container.runtimeVersion.value),
  ].filter(Boolean).length;

  // Flagged, but genuinely not in the way: the submit does not look at any of
  // these. Fields start valid and only turn invalid once they have been
  // touched, so this never accuses someone of a mistake they have not made
  // yet. It reports; it does not gate.
  const issueCount = [
    nothingDetected,
    container.rootDirectoryPath.isValid === false,
    container.exposedPort.isValid === false,
    container.healthCheckEndpoint.isValid === false,
    ...container.environmentVars.map(
      (envVar) => envVar.name.isValid === false || envVar.value.isValid === false
    ),
  ].filter(Boolean).length;
  // Manual Build Method choice is scoped to the path it was made at: browsing
  // to another folder is a change of context, not of preference, so the
  // detection takes over again.
  const userChoseBuildMethodRef = useRef(false);
  // Editing the Start Command, on the other hand, sticks for the whole form —
  // a suggestion never overwrites what the user typed.
  const userEditedStartCommandRef = useRef(false);
  // Clicking a folder is a complete, deliberate change: detect right away.
  // Only typed paths need the debounce.
  const skipDebounceRef = useRef(false);

  /**
   * Auto deploy cannot survive going back to Free.
   *
   * The plan is chosen below this toggle and can be changed after it — so
   * without this, turning it on under Starter and then switching to Free would
   * submit a Free app with auto deploy on. Greying the control is not enough:
   * a disabled input keeps whatever value it had.
   */
  useEffect(() => {
    if (autoDeployAvailable) {
      return;
    }

    setContainer((prev) =>
      prev.autoDeploy.value ? { ...prev, autoDeploy: { value: false } } : prev
    );
  }, [autoDeployAvailable]);

  /**
   * Fills the Start Command with the command the analyzer would run.
   *
   * Three cases where it fills NOTHING, and they are not politeness — in each
   * one, writing the field makes the app worse:
   *
   * - a `Procfile`, or a `start` script in `package.json`: the repository has
   *   already said how it runs, and the buildpack follows it. A command in
   *   this field is baked into the image by the start-command shim, so from
   *   then on the app stops following the repository — the deploy after the
   *   next commit runs what was typed here today.
   * - a static site: the platform serves the built files, and it does that
   *   only while this field is empty. The analyzer's answer there is usually a
   *   dev preview server, which is a worse way to serve the same build.
   *
   * In all three the suggestion is still shown under the field, one click
   * away, because the detection is a guess and the user may know better.
   */
  const applySuggestedStartCommand = (
    assistance: RunAssistance | undefined,
    info: RepositoryBuildInfo | null
  ) => {
    if (
      userEditedStartCommandRef.current ||
      !assistance?.startCommand ||
      !info ||
      info.hasProcfile ||
      info.hasStartScript ||
      info.isStaticSite
    ) {
      return;
    }

    const command = assistance.startCommand;

    // Never over the deterministic suggestion that just landed: that one is
    // derived from the repository's own conventions, and it is the more
    // conservative of the two.
    setContainer((prev) =>
      prev.startCommand.value
        ? prev
        : { ...prev, startCommand: { value: command } }
    );
  };

  /**
   * Puts the variables the analyzer says this project reads into the list.
   *
   * Names only. The values are the customer's secrets and we have none of
   * them — the point is that the app is not created missing a variable it
   * cannot start without, which is the failure this used to produce: a build
   * that ran, a container that crashed, and a message about a port.
   *
   * Re-running replaces the previous suggestions rather than piling a second
   * set on top — except any row that already has a value typed into it, which
   * stopped being a suggestion the moment it was answered.
   */
  const applySuggestedEnvVars = (assistance?: RunAssistance) => {
    const suggested = (assistance?.requiredEnvVars || []).filter(
      (envVar) => envVar && envVar.key
    );
    const previousKeys = suggestedEnvKeysRef.current;

    suggestedEnvKeysRef.current = suggested.map((envVar) => envVar.key);

    setContainer((prev) => {
      const kept = prev.environmentVars.filter(
        (envVar) =>
          !previousKeys.includes(envVar.name.value) || envVar.value.value !== ""
      );
      const present = kept.map((envVar) => envVar.name.value);

      const added: ENV_VAR_FORM[] = suggested
        .filter((envVar) => !present.includes(envVar.key))
        .map((envVar) => ({
          id: Math.random().toString(16).slice(2),
          name: { isValid: true, value: envVar.key, errorMsg: "" },
          value: { isValid: true, value: "", showValue: false },
        }));

      return { ...prev, environmentVars: [...kept, ...added] };
    });
  };

  useEffect(() => {
    if (!selectedRepository || !container.branchName.value) {
      return;
    }

    let cancelled = false;
    // New branch or new folder: the previous manual choice does not carry over.
    userChoseBuildMethodRef.current = false;

    const delay = skipDebounceRef.current ? 0 : 600;
    skipDebounceRef.current = false;

    const timeout = setTimeout(async () => {
      // The detecting state starts with the request, NOT with the keystroke:
      // branch and root dir are free-text, so flipping it at the top of the
      // effect would dim the section on every character typed while nothing
      // is actually being fetched for another 600ms.
      setIsDetecting(true);
      setIsAnalyzing(true);

      // Started together, awaited apart. The analysis reads the repository
      // with a model behind it and is allowed tens of seconds; the stack
      // detection is one directory listing. Waiting for both before showing
      // either would leave the Build Method section spinning for the whole
      // analysis, for an answer that arrived at the start of it.
      const analysis = back4app2
        .repositoryRunAssistance(
          selectedRepository.id,
          container.branchName.value,
          container.rootDirectoryPath.value
        )
        .catch((e) => {
          // Suggestions are a courtesy. Nothing here is worth a message: the
          // form is complete and correct without any of it.
          console.error("error reading the repository analysis", e);

          return undefined;
        });

      let result: RepositoryBuildInfo | null = null;

      try {
        result = await back4app2.repositoryBuildInfo(
          selectedRepository.id,
          container.branchName.value,
          container.rootDirectoryPath.value
        );
      } catch (e) {
        console.error("error checking repository build info", e);
      }

      if (cancelled) {
        return;
      }

      // fail-open: with no answer, keep the classic Dockerfile behavior.
      const dockerfileFound = result ? result.hasDockerfile : true;
      const suggestedStartCommand = result?.suggestedStartCommand;

      setBuildInfo(result);
      setIsDetecting(false);
      setContainer((prev) => ({
        ...prev,
        buildMethod: {
          // With the route off, the form behaves as it did before it existed:
          // always Dockerfile. Pre-selecting BUILDPACK here would submit a
          // preference the deploy then refuses.
          value:
            result && !result.buildpackBuildsEnabled
              ? "DOCKERFILE"
              : !dockerfileFound
              ? "BUILDPACK"
              : userChoseBuildMethodRef.current
              ? prev.buildMethod.value
              : "DOCKERFILE",
        },
        // The suggestion only exists when the buildpack would produce no web
        // process at all, so an empty field is the normal case. It never
        // overwrites a command the user typed.
        startCommand: userEditedStartCommandRef.current
          ? prev.startCommand
          : { value: suggestedStartCommand || "" },
      }));

      const assistance = await analysis;

      if (cancelled) {
        return;
      }

      setIsAnalyzing(false);
      setRunAssistance(assistance);
      applySuggestedEnvVars(assistance);
      applySuggestedStartCommand(assistance, result);
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRepository,
    container.branchName.value,
    container.rootDirectoryPath.value,
  ]);

  const onSelectBuildMethod = (value: BuildMethod) => {
    userChoseBuildMethodRef.current = true;
    setContainer((prev) => ({ ...prev, buildMethod: { value } }));
  };

  const onNavigateToDirectory = (newPath: string) => {
    skipDebounceRef.current = true;
    setContainer((prev) => ({
      ...prev,
      rootDirectoryPath: {
        ...prev.rootDirectoryPath,
        value: newPath,
        isValid: true,
        errorMsg: "",
      },
    }));
  };

  const onChangeStartCommand = (newValue: string) => {
    userEditedStartCommandRef.current = true;
    setContainer((prev) => ({ ...prev, startCommand: { value: newValue } }));
  };

  const handleOnChangeEnvVars = (
    id: string,
    newValue: string,
    type: "name" | "value"
  ) => {
    const newEnvVariableList = container.environmentVars.map((env) => {
      if (id === env.id && type === "name") {
        // use only uppercase letters, underscore (_) and numbers.
        // Ref: https://stackoverflow.com/a/2821183/11070995
        let isValid = !!newValue.match(/(^[A-Z_])[A-Z0-9_]+$/);
        let errorMsg = "";
        if (!isValid) {
          errorMsg =
            "Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers";
        } else {
          errorMsg = "";
        }
        return {
          ...env,
          name: { value: newValue, isValid, errorMsg },
        } as ENV_VAR_FORM;
      } else if (id === env.id && type === "value") {
        let isValid = newValue !== "";
        return {
          ...env,
          value: { ...env.value, value: newValue, isValid },
        } as ENV_VAR_FORM;
      }
      return env;
    });
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const handleAddEnviromentVar = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // validate if already empty entry present
    const emptyVarIdx = container.environmentVars.findIndex(
      (env) => env.name.value === ""
    );
    let newEnvVariableList: ENV_VAR_FORM[] = [...container.environmentVars];
    if (emptyVarIdx !== -1) {
      let newEnvVar: ENV_VAR_FORM = {
        ...container.environmentVars[emptyVarIdx],
      };
      newEnvVar.name.isValid = false;
      newEnvVar.name.errorMsg = "A variable name is must!";
      newEnvVariableList.splice(emptyVarIdx, 1, newEnvVar);
    } else {
      const newEnvVar: ENV_VAR_FORM = {
        id: Math.random().toString(16).slice(2),
        name: { isValid: true, value: "", errorMsg: "" },
        value: { isValid: true, value: "", showValue: false },
      };
      newEnvVariableList = [...container.environmentVars, newEnvVar];
    }
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const handleRemoveEnviromentVar = (id: string) => {
    const newEnvVariableList = container.environmentVars.filter(
      (env) => env.id !== id
    );
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const toggleEnvVariableVisibilty = (id: string) => {
    const newEnvVariableList = container.environmentVars.map((env) => {
      if (env.id === id) {
        return {
          ...env,
          value: { ...env.value, showValue: !env.value.showValue },
        };
      }
      return env;
    });
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const onChangeContainerName = (newValue: string) => {
    userEditedNameRef.current = true;
    // validation
    let isValid = true,
      errorMsg = "";
    if (newValue.trim() === "") {
      isValid = false;
      errorMsg = "Container name is required!";
    } else if (takenNames.includes(newValue.trim())) {
      // Said here, while the field is under the cursor, rather than by the
      // create task several seconds later — same rule, a great deal earlier.
      isValid = false;
      errorMsg = "You already have an app with this name.";
    }
    setContainer((prev) => ({
      ...prev,
      containerName: {
        ...prev.containerName,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  // A name that is not in the repository is not caught at creation today: the
  // app is created, and the failure surfaces much later, in the deploy, as a
  // clone error — after the stack detection already reported "no Dockerfile
  // found" for a branch that never existed. So it is checked here, but only
  // against a list we actually have.
  const validateBranchName = (name: string) => {
    if (name.trim() === "") {
      return {
        isValid: false,
        errorMsg: "Repository branch name is required!",
      };
    }

    if (hasBranchList && branches.length > 0 && !branches.includes(name)) {
      return {
        isValid: false,
        errorMsg: `This repository has no branch named "${name}".`,
      };
    }

    return { isValid: true, errorMsg: "" };
  };

  // The list usually arrives after the field is already filled with the default
  // branch, and a Refresh can change what exists — so the verdict is recomputed
  // whenever the list does.
  useEffect(() => {
    // Never on the image route. There is no branch there, and no field on
    // screen to show a verdict about one — so this only ever marked an empty
    // name invalid and left the error to surface on whatever OTHER field
    // happened to read `branchName`.
    if (isImageSource) {
      return;
    }

    setContainer((prev) => {
      const { isValid, errorMsg } = validateBranchName(prev.branchName.value);

      if (
        prev.branchName.isValid === isValid &&
        prev.branchName.errorMsg === errorMsg
      ) {
        return prev;
      }

      return { ...prev, branchName: { ...prev.branchName, isValid, errorMsg } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, hasBranchList, isImageSource]);

  const onChangeBranchName = (newValue: string) => {
    // validation
    const { isValid, errorMsg } = validateBranchName(newValue);
    setContainer((prev) => ({
      ...prev,
      branchName: { ...prev.branchName, value: newValue, isValid, errorMsg },
    }));
  };

  const onChangeHealthCheckEndpoint = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    if (newValue[0] !== "/") {
      newValue = `/${newValue}`;
    }
    setContainer((prev) => ({
      ...prev,
      healthCheckEndpoint: {
        ...prev.healthCheckEndpoint,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  const onChangeRootDirectorPath = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    newValue = newValue.trim();
    if (!newValue.startsWith("./")) {
      if (newValue.startsWith(".") || newValue.startsWith("/")) {
        newValue = newValue.substring(1);
      }
      newValue = `./${newValue}`;
    }
    setContainer((prev) => ({
      ...prev,
      rootDirectoryPath: {
        ...prev.rootDirectoryPath,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  const onChangePort = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    let val = +newValue;
    if (isNaN(val) || !(Number.isInteger(val) && val > 0 && val <= 65535)) {
      isValid = false;
      errorMsg = "Please provide a valid port between 0 to 65535.";
      newValue = "";
    }
    setContainer((prev) => ({
      ...prev,
      exposedPort: { ...prev.exposedPort, value: newValue, isValid, errorMsg },
    }));
  };

  /**
   * Puts the cursor in the name field once it is actually on screen.
   *
   * The App card is closed while everything in it is valid, so at the moment
   * the submit rejects a name the input does not exist yet — a direct `focus()`
   * here landed on nothing. Setting the field invalid is what opens the card;
   * this runs after that render, when there is something to focus.
   */
  const focusNameField = () => {
    setTimeout(() => nameRef.current?.focus(), 0);
  };

  const handleFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    // validate container name
    if (container.containerName.value === "") {
      focusNameField();
      let isValid = false;
      let errorMsg = "Container name is required!";
      setContainer((prev) => ({
        ...prev,
        containerName: { ...prev.containerName, isValid, errorMsg },
      }));
      return;
    }
    // The list may have arrived while the form was open, or the name may have
    // been taken in another tab. Cheap to re-check, and the alternative is a
    // create task that fails on the unique index.
    if (takenNames.includes(container.containerName.value.trim())) {
      focusNameField();
      setContainer((prev) => ({
        ...prev,
        containerName: {
          ...prev.containerName,
          isValid: false,
          errorMsg: "You already have an app with this name.",
        },
      }));
      return;
    }

    // A pin the API would reject: stop here rather than let the whole
    // create-app task fail on a typo in one optional field.
    if (
      container.runtimeVersion.value &&
      !isValidRuntimePin(container.runtimeVersion.value)
    ) {
      return;
    }

    // Same check the field runs, at the last moment it can still stop an app
    // from being created against a branch that does not exist. There is no
    // branch on the image route, and an empty name fails this check — which
    // would block Deploy with no field on screen to explain why.
    if (!isImageSource) {
      const branchCheck = validateBranchName(container.branchName.value);

      if (!branchCheck.isValid) {
        setContainer((prev) => ({
          ...prev,
          branchName: { ...prev.branchName, ...branchCheck },
        }));
        return;
      }
    }

    pushGTMEvent("create_app_containers", true);
    // form containerType value
    let envVars: EnvVarInput[] = visibleEnvVars
      .filter((env) => env.name.value !== "")
      .map((env) => ({ key: env.name.value, value: env.value.value }));
    const containerPayload: ContainerType = {
      name: container.containerName.value,
      selectedRepo: selectedRepository,
      // Empty on the image route, and dropped by the caller: there is no
      // branch, no root directory and no build method to send.
      branch: isImageSource ? "" : container.branchName.value,
      rootDirPath: isImageSource ? "" : container.rootDirectoryPath.value,
      autoDeploy: isImageSource ? false : container.autoDeploy.value,
      envVars,
      exposedPort: +container.exposedPort.value,
      healthCheckEndpoint: container.healthCheckEndpoint.value,
      startCommand: isImageSource
        ? undefined
        : container.startCommand.value || undefined,
      builderPreference: isImageSource
        ? undefined
        : container.buildMethod.value,
      // Only ever what someone typed: empty means "follow the repository",
      // which is what an app that was never pinned has to keep meaning.
      runtimeVersion:
        container.buildMethod.value === "BUILDPACK" &&
        container.runtimeVersion.value
          ? container.runtimeVersion.value
          : undefined,
      // Detected, never typed: the command that serves a static site names a
      // file the platform injects at build time, so it stays out of the form.
      // Still sent when the user typed a Start Command — `cnbBuild` consults
      // the flag only for an empty command, so it changes nothing about this
      // build and is what lets clearing the command in Settings later fall
      // back to static serving instead of to no web process at all.
      isStaticSite: isStaticSite || undefined,
      // Only with the database: without it there is nothing to name.
      databaseUrlEnvKey: hasManagedDatabase ? managedDatabaseKey : undefined,
    };
    onSubmit(containerPayload);
  };

  // What each card says while it is closed — and closed is how they all start.
  //
  // The step used to be the form itself: every field of it, open, in one
  // column, with the plan and the total at the end of the scroll. Almost none
  // of it needed a decision — the repository was read and the answers filled
  // in — so the work of the screen was reading twenty controls to find the two
  // that mattered. Now the answers are the screen and the controls are behind
  // an Edit, which is the same information at a fraction of the load.
  const buildMethodSummary =
    container.buildMethod.value === "DOCKERFILE"
      ? "Dockerfile"
      : buildInfo?.detectedStack
      ? `${buildInfo.detectedStack} Buildpack`
      : "Buildpacks";

  /**
   * The mark beside that answer, the same one the row it came from carries.
   *
   * Closed is how this card starts and how it usually stays, so the summary is
   * what most people ever read of it — and it was the one place the build
   * method was a bare word. Docker's whale for the Dockerfile route, the
   * stack's own mark for a buildpack, and the layers glyph when the stack is
   * not known yet, which is exactly what the rows behind Edit show.
   *
   * In the brand's colour, like the rows: these marks are recognised by colour
   * before they are read, and a grey Docker whale is a different thing.
   */
  const buildMethodLogo = (() => {
    const { Logo, color } =
      container.buildMethod.value === "DOCKERFILE"
        ? DOCKER_LOGO
        : getStackLogo(buildInfo?.detectedStack) || {
            Logo: BuildpackLayersIcon,
            color: undefined,
          };

    return (
      <Logo
        width="14"
        height="14"
        fill="currentColor"
        className="flex-none"
        style={color ? { color } : undefined}
      />
    );
  })();

  // The pin when there is one, otherwise what the repository resolved to. Both
  // are named with the stack, because "22" on its own says nothing.
  const runtimeSummary =
    container.buildMethod.value !== "BUILDPACK"
      ? "from your Dockerfile"
      : container.runtimeVersion.value
      ? `${buildInfo?.detectedStack || "pinned"} ${container.runtimeVersion.value}`
      : buildInfo?.runtimeVersion?.version
      ? `${buildInfo?.detectedStack || ""} ${buildInfo.runtimeVersion.version}`.trim()
      : "auto";

  const startCommandSummary =
    container.startCommand.value ||
    (isStaticSite ? "static output (default)" : "repository default");

  // Counts the managed rows too. The card is collapsed by default, so a
  // summary reading "None set" over a section that visibly holds DATABASE_URL
  // is the card contradicting its own contents.
  const envVarCount =
    visibleEnvVars.length + managedEnvVarKeys.length;

  const envVarSummary = missingRequiredEnvIds.length
    ? `${envVarCount} found · ${missingRequiredEnvIds.length} without a value`
    : envVarCount
    ? `${envVarCount} set`
    : "None set";

  // A card opens itself when something inside it is wrong, and stays open while
  // it is: a closed card cannot report an error, and an error nobody can see is
  // a submit that refuses without saying why.
  const appInvalid =
    container.containerName.isValid === false ||
    (!isImageSource &&
      (container.branchName.isValid === false ||
        container.rootDirectoryPath.isValid === false));

  const buildInvalid =
    (!!container.runtimeVersion.value &&
      !isValidRuntimePin(container.runtimeVersion.value)) ||
    container.exposedPort.isValid === false ||
    container.healthCheckEndpoint.isValid === false;

  // The database variable's name is not checked here: the API owns that rule
  // and refuses the create, before any charge, with a message that names it.
  const deployInvalid =
    visibleEnvVars.some(
      (envVar) => envVar.name.isValid === false || envVar.value.isValid === false
    );

  return (
    // No implicit submission: the form has no submit button, so Enter in any
    // field would fire the browser's default submit and reload the page, which
    // resets the form and reads as "something was sent". Creating an app goes
    // through the Create button and handleFormSubmit, never through Enter.
    <form
      className={`${styles.containerForm} flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6`}
      onSubmit={(e) => e.preventDefault()}
    >
      {selectedRepository && (
        <RepositoryBrowser
          open={isBrowsingRepository}
          repositoryId={selectedRepository.id}
          branchName={container.branchName.value}
          initialPath={container.rootDirectoryPath.value}
          onClose={() => setIsBrowsingRepository(false)}
          onSelect={onNavigateToDirectory}
        />
      )}

      {/* The review: what we worked out about this repository, one card per
          thing someone might want to change. */}
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        {/* Above the cards, where the thing it is about to interrupt is. Its
            own icon carries the tone; a coloured bar down the edge was a second
            voice saying the same word louder. */}
        {nothingDetected ? (
          <div className="rounded-card bg-navy-700 px-4 py-4">
            <NoAppDetectedNotice
              rootDirectoryPath={container.rootDirectoryPath.value}
              onBrowseRepository={() => setIsBrowsingRepository(true)}
              buildpacksAvailable={buildpacksAvailable}
            />
          </div>
        ) : null}

        <ReviewCard
          title="App"
          invalid={appInvalid}
          // The one thing on this screen that is not filled in for you: the
          // image route has no repository to name the app after. An empty
          // answer behind a closed card is a required field nobody can see, so
          // the card starts open until there is something in it.
          defaultOpen={!container.containerName.value}
          rows={
            <>
              <SummaryRow label="Name" value={container.containerName.value || '—'} />
              {isImageSource && imageReference ? (
                <SummaryRow label="Image" value={imageReference} />
              ) : null}
              {!isImageSource && selectedRepository ? (
                <SummaryRow label="Repository" value={selectedRepository.fullName} />
              ) : null}
              {!isImageSource ? (
                <>
                  <SummaryRow label="Branch" value={container.branchName.value || '—'} />
                  {/* The folder the notice above is about. Marked only while
                      that notice is up — a red path with nothing explaining it
                      would read as a rejected value. */}
                  <SummaryRow
                    label="Root directory"
                    value={container.rootDirectoryPath.value || './'}
                    attention={nothingDetected}
                  />
                </>
              ) : null}
            </>
          }
        >
          <TextInput
            value={container.containerName.value}
            label="Name"
            name={"name"}
            onChange={(e) => onChangeContainerName(e.target.value)}
            placeholderText="Give a name"
            ref={nameRef}
            isError={!container.containerName.isValid}
            errorMessage={container.containerName.errorMsg}
            isRequired={container.containerName.isRequired}
          />
          {/* The image route has no branch and no folder to point at — the
              artifact is already built — so the fields are absent rather than
              shown empty. */}
          {!isImageSource ? (
            <div className="grid grid-cols-2 gap-x-5 gap-y-5 [@media(max-width:767.98px)]:grid-cols-1">
              {/* Typeable on purpose — see BranchSelect: the listing is capped
                  and can fail, and neither must block creating an app. */}
              <BranchSelect
                value={container.branchName.value}
                branches={branches}
                onChange={onChangeBranchName}
                onRefresh={loadBranches}
                isLoading={isLoadingBranches}
                isError={!container.branchName.isValid}
                errorMessage={container.branchName.errorMsg}
              />
              {/* Browse is always offered, whatever the repository looks like.
                  It is deliberately NOT disabled when the current path has no
                  subfolders: we only learn that after a detection round, so the
                  control would go grey a second after loading — and an empty
                  listing is also what a mistyped path produces, exactly when the
                  browser is the way out. The modal says so in words instead. */}
              <TextInput
                value={container.rootDirectoryPath.value}
                label="Root directory"
                name="rootDirPath"
                onChange={(e) => onChangeRootDirectorPath(e.target.value)}
                placeholderText="./"
                infoTip={ROOT_DIRECTORY_TIP}
                hintClassName="hidden sm:block"
                isError={!container.rootDirectoryPath.isValid}
                errorMessage={container.rootDirectoryPath.errorMsg}
                endAdornment={
                  <button
                    type="button"
                    className={`${styles.groupInputAddBtn} whitespace-nowrap`}
                    onClick={() => setIsBrowsingRepository(true)}
                  >
                    <FolderIcon /> Browse folders
                  </button>
                }
              />
            </div>
          ) : null}
        </ReviewCard>

        <ReviewCard
          title="Build & run"
          // Said once, on the card that holds the answers it produced, instead
          // of a status line over every field it filled in.
          badge={
            !isImageSource && !isDetecting && !nothingDetected && !!buildInfo
              ? 'detected'
              : undefined
          }
          isBusy={!isImageSource && isDetecting}
          invalid={buildInvalid}
          rows={
            <>
              {/* While the repository is being read, these three say nothing
                  rather than something wrong.

                  `buildMethod` starts at DOCKERFILE because that is what this
                  form did before buildpacks existed, so the closed card
                  asserted "Dockerfile" — with Docker's whale beside it — for
                  as long as the read took, and then changed its mind to
                  "Node.js Buildpack". Runtime and Start command had the same
                  problem in a quieter form: both read as "Not detected", which
                  is a verdict, and nothing had been detected yet because
                  nothing had been looked at.

                  A re-read counts too. Change the branch or the folder and
                  these three are about a folder that is no longer the one
                  chosen; they are not right again until the new answer lands. */}
              {!isImageSource ? (
                <>
                  <SummaryRow
                    label="Build method"
                    value={isDetecting ? <SummaryPending /> : (
                      <span className="inline-flex items-center gap-2">
                        {buildMethodLogo}
                        {buildMethodSummary}
                      </span>
                    )}
                  />
                  <SummaryRow
                    label="Runtime"
                    value={isDetecting ? <SummaryPending /> : runtimeSummary}
                  />
                  <SummaryRow
                    label="Start command"
                    value={isDetecting ? <SummaryPending /> : startCommandSummary}
                  />
                </>
              ) : null}
              <SummaryRow
                label="Port · Health check"
                value={`${container.exposedPort.value || 'auto'} · ${
                  container.healthCheckEndpoint.value || 'off'
                }`}
              />
            </>
          }
        >
          {/* Nothing deployable here: the build method is not offered at all.
              There is no choice left to make — Dockerfile is already disabled
              (there is none) and Buildpacks has nothing to build — so a radio
              group would only ask the user to pick between two dead ends. The
              notice itself sits above these cards, because a blocking state
              read halfway down a scroll is a blocking state nobody reads. */}
          {/* Opened, this card was two stray fields and no word about the three
              that were missing — while closed it still listed Build method,
              Runtime and Start command among its rows. One line, and it says
              only what the card itself cannot: why it is short and what brings
              the rest back. Not what state we are in — the notice above the
              cards already says that, and saying it twice is what this whole
              pass is removing. */}
          {!isImageSource && nothingDetected ? (
            <div className="text-ds-115 leading-[1.5] text-navy-200">
              Build method, runtime and start command appear once this folder
              has an app in it.
            </div>
          ) : null}

          {!isImageSource && !nothingDetected ? (
            <>
              <div className="flex flex-col gap-2">
                <span className="text-ds-135 font-medium leading-[1.4] text-white">Build method</span>
                {/* Six, not four. Each of these is a field with a line of its
                    own under it, and at 16px the gap between one group and the
                    next was the same as the gap inside a group — so the whole
                    section read as one paragraph of controls rather than as
                    three questions. The space came from the empty detection
                    band that used to sit above them. */}
                <div className="space-y-6">
                  <BuildMethodSelector
                    value={container.buildMethod.value}
                    onChange={onSelectBuildMethod}
                    buildpacksAvailable={buildpacksAvailable}
                    isDetecting={isDetecting}
                    hasDetectionResult={hasDockerfile !== null}
                    dockerfileDisabled={isRealDirectory && hasDockerfile === false}
                    dockerfileSubtitle={
                      isRealDirectory && hasDockerfile === false
                        ? "No Dockerfile in this folder."
                        : undefined
                    }
                    detectedStack={buildInfo?.detectedStack}
                    showDockerfileIgnoredWarning={hasDockerfile === true}
                  />
                  {/* Detection stated as a default, not as a verdict. The Start
                      Command below is the way out of it: `suggestStartCommand`
                      returns nothing for a static site, so the field arrives empty,
                      and `cnbBuild` reads `isStaticSite` only while it stays empty.
                      Typing a command therefore wins, clearing it goes back to
                      static serving — the same bargain Settings already offers. */}
                  {isStaticSite && (
                    <div className="text-ds-115 leading-[1.5] text-navy-200">
                      This looks like a static site — we'll serve the built files for
                      you.<span className="hidden sm:inline"> Set a Start Command below to run your own server instead.</span>
                    </div>
                  )}
                  {/* Only on the buildpack path: a Dockerfile names its own runtime
                      in its FROM line, and a field that silently did nothing there
                      would be worse than no field. */}
                  {container.buildMethod.value === "BUILDPACK" && (
                    <RuntimeVersionField
                      detectedStack={buildInfo?.detectedStack}
                      resolved={buildInfo?.runtimeVersion}
                      value={container.runtimeVersion.value}
                      onChange={(value) =>
                        setContainer((prev) => ({
                          ...prev,
                          runtimeVersion: { value },
                        }))
                      }
                    />
                  )}
                  {/* Asking how to start an app we haven't found is noise — hence
                      the `nothingDetected` gate above. A static site is the
                      opposite case: the detection is a guess the user may disagree
                      with, and a repository whose server we failed to see had no
                      way to say so here while Settings, on the very same app,
                      offered the field and told them to use it. */}
                  {container.buildMethod.value === "BUILDPACK" && (
                    <div className="space-y-2">
                      <TextInput
                        value={container.startCommand.value}
                        label="Start Command"
                        name="startCommand"
                        onChange={(e) => onChangeStartCommand(e.target.value)}
                        placeholderText="e.g. gunicorn app:app"
                        infoText={startCommandHelp({
                          isStaticSite,
                          isNarrowScreen,
                          hasSuggestion: !!analyzerStartCommand,
                        })}
                        infoTip={START_COMMAND_TIP}
                        isError={false}
                        errorMessage=""
                      />
                      {/* Offered, never typed in for you — and the difference is
                          not politeness. `suggestStartCommand` returns nothing on
                          purpose for a repository that already declares how it
                          starts (a Procfile, a `start` script): filling the field
                          bakes that command into the image through the start
                          shim, and the app stops following the repository from
                          then on. So the analyzer's answer is shown as what we
                          WOULD run, and it takes a click to make it a setting. */}
                      {analyzerStartCommand ? (
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-ds-115 leading-[1.5] text-navy-200">
                          <span>
                            We read this project as starting with{" "}
                            <code className="text-white">
                              {analyzerStartCommand}
                            </code>
                            .
                          </span>
                          <button
                            type="button"
                            className="b4-focus underline underline-offset-2 text-white hover:opacity-80 transition-opacity"
                            onClick={() =>
                              onChangeStartCommand(analyzerStartCommand)
                            }
                          >
                            Use it
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-navy-600" />
            </>
          ) : null}

          <div className="grid grid-cols-2 gap-x-5 gap-y-5 [@media(max-width:767.98px)]:grid-cols-1">
            <TextInput
              value={`${container.exposedPort.value}`}
              label="Port"
              name="exposedPort"
              onChange={(e) => onChangePort(e.target.value)}
              placeholderText="8080"
              infoTip={PORT_TIP}
              hintClassName="hidden sm:block"
              isError={!container.exposedPort.isValid}
              errorMessage={container.exposedPort.errorMsg}
            />
            <TextInput
              value={container.healthCheckEndpoint.value}
              label="Health check"
              name="healthCheckEndpoint"
              onChange={(e) => onChangeHealthCheckEndpoint(e.target.value)}
              placeholderText="/checkpath"
              infoTip={HEALTH_CHECK_TIP}
              hintClassName="hidden sm:block"
              // Its OWN validity, not the branch's. Copy-paste from the branch
              // field above meant the health input reported "Repository branch
              // name is required!" — and on the container-image route, where
              // there is no branch at all, it said so permanently, about a
              // field that was perfectly valid.
              isError={!container.healthCheckEndpoint.isValid}
              errorMessage={container.healthCheckEndpoint.errorMsg}
            />
          </div>
        </ReviewCard>

        <ReviewCard
          title="Deploy settings"
          invalid={deployInvalid}
          // Opens itself once when the analysis finds variables without a
          // value — to show what was found, not to accuse. It can be closed.
          attention={missingRequiredEnvIds.length > 0}
          badge={requiredEnvKeys.length ? "detected" : undefined}
          rows={
            <>
              <SummaryRow
                label="Auto deploy on push"
                value={
                  isImageSource
                    ? 'Manual'
                    : container.autoDeploy.value
                    ? 'On'
                    : 'Off'
                }
              />
              <SummaryRow label="Environment variables" value={envVarSummary} />
            </>
          }
        >
          {/* Gone, not disabled, on the image route: there is no push to
              listen to, so a greyed toggle would read as a feature we took
              away rather than one that does not apply. The line says what
              happens instead. */}
          {isImageSource ? (
            <div className="text-ds-115 leading-[1.5] text-navy-200">
              Deploys are manual on this route. Publish a new image and
              redeploy from the app's page when you want it picked up.
            </div>
          ) : (<>
            <ToggleSwitchCheckbox
              value={container.autoDeploy.value}
              onChange={() => {
                const turningOn = !container.autoDeploy.value;

                setContainer((prev) => ({
                  ...prev,
                  autoDeploy: { value: !prev.autoDeploy.value },
                }));

                // On a plan without it, turning it on moves the app to the
                // cheapest paid plan — the click says what the person wants.
                if (turningOn && !autoDeployAvailable) {
                  onRequirePaidPlan?.();
                }
              }}
              labelText="Auto deploy on push"
              // The label says it on a phone; the paid-plan line below is the
              // one that changes something.
              description={isNarrowScreen ? undefined : "Deploy on every repository push. Off means you trigger every deploy yourself."}
              disabled={!autoDeployAvailable && !onRequirePaidPlan}
            />
            {!autoDeployAvailable ? (
              // Quiet, under the switch: that it is a paid-plan feature, and
              // what turning it on will do to the plan.
              <span className="-mt-2 block text-ds-115 leading-[1.5] text-navy-300">
                {onRequirePaidPlan
                  ? `Available on paid plans — turning it on selects ${paidPlanName || 'a paid plan'}.`
                  : 'Available on paid plans.'}
              </span>
            ) : null}
          </>)}

          <div className="border-t border-navy-600" />

          <div>
            {/* "Your App will have access to these variables at both build
                time and runtime" was its own line under this label. Its own
                note here called it "a sentence nobody needs to read twice",
                and then kept it on screen every time. It is behind the (i)
                now — when the variables reach the app is a fact about the
                platform, the same on every app, and it changes nothing about
                which names anyone types below. */}
            <span className="block mb-3 sm:mb-4 text-ds-135 font-medium leading-[1.4] text-white">
              Environment variables{' '}
              <InfoTip label="About environment variables">
                These reach your app at build time and at runtime. Changing one
                later takes a redeploy.
              </InfoTip>
            </span>
            {/* The waiting line stays: a section that is empty and then fills
                itself seconds later, with nothing said in between, reads as
                the form changing its mind.

                What followed it does not. "We found these in your code. Fill
                in the values now or later in Settings, and remove any that do
                not apply." was three instructions for a list whose rows say
                where they came from under each name, whose values are
                obviously typed into the fields beside them, and whose rows
                each carry a button to remove them. */}
            {isAnalyzing ? (
              <div className="text-ds-115 leading-[1.5] text-navy-200 mb-4">
                Reading your repository for the variables it needs…
              </div>
            ) : null}
            {/* Set by us, shown anyway.
                Above the editable rows because it is the one the app will
                actually connect with — and because a customer who does not
                know it exists is a customer about to add a second one. The
                value is deliberately not a field: the database is provisioned
                on the first deploy, so there is nothing to show yet, and it
                would never be theirs to type. */}
            {/* One list, rows split by a hairline: the fields already say
                what each row is, so the gap between rows was only there to
                tell them apart — a line does that in a third of the height. */}
            <div className="flex flex-col divide-y divide-navy-600/60 mb-4">
            {hasManagedDatabase ? (
              <div className="py-4 first:pt-0 flex flex-col gap-3">
                {/* Same grid as the rows below it, plus an empty slot where
                    their trash button sits, so the columns line up — there is
                    nothing to delete here: the database owns the variable. */}
                <div className="flex [@media(max-width:767.98px)]:flex-col justify-between items-start [@media(max-width:767.98px)]:items-stretch gap-3 md:gap-0">
                  <div className={styles.inputGroupDiv + " flex-1"}>
                  <div className="min-w-0">
                    {/* The name is theirs to change — some code reads
                        POSTGRES_URL — the value never is, and is never shown. */}
                    <TextInput
                      value={managedDatabaseKey}
                      label="Name"
                      name="env-name-managed-database"
                      onChange={(e) => {
                        userEditedDatabaseKeyRef.current = true;
                        setDatabaseKeyInput(e.target.value.trim());
                      }}
                      placeholderText={DEFAULT_DATABASE_URL_ENV_KEY}
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-ds-135 font-medium leading-[1.4] text-white mb-1.5">
                      Value
                    </span>
                    <div className="h-11 lg:h-[38px] flex items-center rounded-control border border-navy-600 bg-navy-700 px-3 text-ds-115 text-navy-200">
                      Set by Back4app
                    </div>
                  </div>
                  </div>
                  <div aria-hidden="true" className="hidden md:block flex-none ml-4 w-[42px]" />
                </div>
                <div className="text-ds-115 leading-[1.5] text-navy-300">
                  Filled in by Back4app on the first deploy. Rename it if your
                  code reads a different name.
                </div>
              </div>
            ) : null}
            {/* Enviroment variable list */}
            {visibleEnvVars.map((enviromentVar) => {
              const { id, name, value } = enviromentVar;
              const isDatabaseChoice =
                !hasManagedDatabase &&
                !!databaseSuggestionKey &&
                name.value === databaseSuggestionKey &&
                value.value === "";
              return (
                <div key={id} className="py-4 first:pt-0">
                <div className="flex [@media(max-width:767.98px)]:flex-col justify-between items-start [@media(max-width:767.98px)]:items-stretch gap-3 md:gap-0">
                  <div className={styles.inputGroupDiv + " flex-1"}>
                    <TextInput
                      value={name.value}
                      label="Name"
                      name={`env-name-${id}`}
                      onChange={(e) =>
                        handleOnChangeEnvVars(id, e.target.value.trim(), "name")
                      }
                      placeholderText="EXAMPLE_NAME"
                      isError={!name.isValid}
                      errorMessage={name.errorMsg}
                    />
                    <TextInput
                      type={value.showValue ? "text" : "password"}
                      value={value.value}
                      label="Value"
                      name={`env-value-${id}`}
                      onChange={(e) =>
                        handleOnChangeEnvVars(id, e.target.value, "value")
                      }
                      placeholderText="A1B2C3D4E5F6"
                      endAdornment={
                        <span
                          className="cursor-pointer"
                          onClick={() => toggleEnvVariableVisibilty(id)}
                        >
                          {value.showValue ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </span>
                      }
                      helpText={value.showValue ? "Will be encrypted." : ""}
                    />
                  </div>
                  {/* Mirrors the field's own label-then-control stack rather
                      than guessing a top margin: the value input grows a help
                      line when the value is revealed, and a fixed `mt-8` put the
                      button below the box it belongs to. An empty label of the
                      same size holds the same space by construction. */}
                  <div className="flex flex-col gap-1.5 ml-4 [@media(max-width:767.98px)]:ml-0 [@media(max-width:767.98px)]:w-full">
                    <span aria-hidden="true" className="hidden md:block text-ds-135 leading-[1.4] select-none">&nbsp;</span>
                    <button
                      type="button"
                      className="b4-focus text-state-err flex justify-center items-center gap-2 px-3 border border-state-err/60 rounded-control hover:bg-state-err/10 h-11 lg:h-[38px] [@media(max-width:767.98px)]:w-full text-center transition-colors duration-[120ms]"
                      onClick={() => handleRemoveEnviromentVar(id)}
                    >
                      <TrashIcon />
                      <span className="hidden [@media(max-width:767.98px)]:inline text-sm">Remove variable</span>
                    </button>
                  </div>
                </div>
                {isDatabaseChoice ? (
                  // A connection string is a decision, not a blank: the
                  // database can come from Back4app, or be the customer's own.
                  //
                  // A note under the field, not a panel beside it. A border, a
                  // fill and a white heading is the shape this form uses for
                  // something that went wrong — and nothing has. The row it
                  // hangs from is a variable the analysis recognised, and the
                  // offer is a convenience. So: no border, a fill light enough
                  // to group the lines rather than frame them, and the first
                  // line at the same weight as the second, because a heading
                  // over one sentence was hierarchy invented for its own sake.
                  <div className="mt-3 rounded-control bg-navy-700/40 px-4 py-3 flex [@media(max-width:767.98px)]:flex-col items-start [@media(max-width:767.98px)]:items-stretch gap-3">
                    <DatabaseIcon size={16} className="flex-none mt-0.5 text-navy-300" />
                    <div className="flex-1 min-w-0 text-ds-115 leading-[1.5] text-navy-200">
                      <span className="block font-medium text-navy-100">
                        Your code needs a database
                      </span>
                      {/* One line, and only the half the button cannot say.
                          It read "Add a Back4app database for $5/month and we
                          connect it for you, or paste the connection string of
                          your own above" — next to a button already reading
                          "Add database · $5/mo". The price twice, the action
                          twice, and the other option buried at the end of the
                          sentence that was selling against it. The field above
                          is where a connection string goes; what is worth
                          saying is that the bought one fills it in. */}
                      {onAddDatabase
                        ? 'We connect it and fill this in — or paste your own above.'
                        : 'Paste your connection string above.'}
                    </div>
                    {onAddDatabase ? (
                      <DsButton
                        variant="ghost"
                        size="md"
                        onClick={onAddDatabase}
                        className="flex-none !text-action-soft hover:!text-white"
                      >
                        {databasePrice
                          ? `Add database · $${databasePrice}/mo`
                          : "Add database"}
                      </DsButton>
                    ) : null}
                  </div>
                ) : null}
                </div>
              );
            })}
            </div>
            {/* Add button for Environment varible */}
            <a
              onClick={handleAddEnviromentVar}
              type="button"
              href="/#"
              className={styles.groupInputAddBtn}
            >
              {" "}
              <PlusIcon /> Add variable
            </a>
          </div>
        </ReviewCard>

        {/* What is sold on top of the instance. Last, because it is the only
            card that asks for a decision nobody has made yet. */}
        {extras}
      </div>

      {/* The rail: what it runs on, what it costs, and the button that buys it
          — one object, pinned beside the review rather than waiting at the end
          of it. The price used to be the last block of a long scroll, which is
          why the primary button had to spend its first press scrolling there. */}
      <aside className="w-full lg:w-[336px] flex-none flex flex-col gap-3 lg:sticky lg:top-5">
        <div className="rounded-card bg-navy-800 border border-navy-500 overflow-hidden">
          {rail}

          <div className="px-5 py-5 border-t border-navy-600 flex flex-col gap-3">
            {/* Four registers, and the strongest one wins: what the last press
                was refused for, what stops the submit, then what merely worries
                us, then nothing at all.

                The refusal sits here, beside the button that was pressed. It
                used to be a line above the review, and with the page scrolled
                down to the button it was off screen: the press looked like it
                had done nothing. */}
            {creatingAppErrorMessage ? (
              <div role="alert" className="flex items-start gap-2 text-ds-125 leading-[1.5]">
                <span className="w-[18px] h-[18px] flex-none mt-px rounded-full bg-state-err/[0.14] text-state-err flex items-center justify-center">
                  <ErrorIcon width="11px" height="11px" />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-state-err">{creatingAppErrorMessage}</div>
                  <div className="text-navy-200">Nothing was created — your settings are still here.</div>
                </div>
              </div>
            ) : blockingCount > 0 ? (
              <div className="flex items-start gap-2 font-courrier text-ds-115 text-state-err">
                <TriangleExclamationIcon width="13px" height="13px" />
                {blockingCount === 1
                  ? 'Fix the highlighted field to continue'
                  : `Fix ${blockingCount} highlighted fields to continue`}
              </div>
            ) : issueCount > 0 ? (
              // The detection is a guess, and a repository we failed to read
              // may still build — so this says how many, and that they are not
              // in the way.
              <div className="flex items-start gap-2 font-courrier text-ds-115 text-state-warn">
                <TriangleExclamationIcon width="13px" height="13px" />
                {issueCount} warning{issueCount === 1 ? '' : 's'} · you can continue anyway
              </div>
            ) : null}

            {/* The wait belongs on the button that started it. It used to
                replace the whole step with a centred spinner card, which threw
                away the settings you had just reviewed and gave you nothing to
                look at while the app was made — and if it failed you came back
                to a screen you had already left. */}
            <DsButton
              variant={submitVariant}
              size="touch"
              onClick={handleFormSubmit}
              isDisabled={isCreatingApp || isSubmitDisabled}
              className="w-full"
            >
              {isCreatingApp ? 'Working…' : submitLabel}
            </DsButton>

            {footerTitle || footerSubtitle ? (
              <p className="m-0 text-center text-ds-115 leading-[1.5] text-navy-300">
                {footerTitle}
                {footerTitle && footerSubtitle ? ' ' : null}
                {footerSubtitle}
              </p>
            ) : null}
          </div>
        </div>

        {/* Outside the card: it undoes the screen, not the purchase. */}
        <DsButton
          variant="outline"
          size="lg"
          onClick={onCancel}
          isDisabled={isCreatingApp}
          className="w-full"
        >
          Cancel
        </DsButton>
      </aside>
    </form>
  );
};

/**
 * One line of a closed card: what a setting is, and what it is set to.
 *
 * The value is in the mono face and the label is not, so a column of them
 * reads as data against its own headings rather than as prose.
 */
/**
 * Stands in for an answer that is being read, in a row that has one.
 *
 * A bar rather than a dash or an empty cell: those are answers too — "none"
 * and "nothing here" — and this row does not have one yet.
 */
const SummaryPending = () => (
  <Skeleton className="h-3 w-[110px] rounded align-middle" />
);

const SummaryRow = ({ label, value, attention = false }: {
  label: string,
  value: React.ReactNode,
  /**
   * The answer on this row is the one the notice above is about.
   *
   * Only the value is coloured, never the label: the row is not broken — the
   * folder is a real folder and a valid answer — it is the one the reader has
   * to look at, and the card is closed, so without this the notice names a
   * path that appears nowhere on screen.
   */
  attention?: boolean,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-3 border-b border-navy-700 last:border-b-0">
    <span className="flex-none text-ds-135 text-navy-200">{label}</span>
    <span
      className={`min-w-0 font-courrier text-ds-125 text-right truncate ${attention ? 'text-state-err' : 'text-white'}`}
      title={typeof value === 'string' ? value : undefined}
    >
      {value}
    </span>
  </div>
);

/**
 * A block of the review: its answers while it is closed, its fields once it
 * is opened.
 *
 * Closed is the default, and that is the whole point of the screen — the
 * repository was read, every answer is filled in, and the ones worth changing
 * are behind one press each. It was a form of open sections before, where
 * finding the two fields that needed a decision meant reading twenty that did
 * not.
 *
 * It opens itself when what it holds is invalid, and stays open while it is —
 * a closed card cannot show a field error, and the submit refuses on some of
 * them.
 */
const ReviewCard = ({ title, badge, isBusy = false, invalid = false, attention = false, defaultOpen = false, rows, children }: {
  title: string,
  badge?: string,
  isBusy?: boolean,
  invalid?: boolean,
  /** Something worth seeing arrived: opens the card once, and it can be closed. */
  attention?: boolean,
  /** Read once, at mount: filling the field in must not close the card. */
  defaultOpen?: boolean,
  rows: React.ReactNode,
  children: React.ReactNode,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = open || invalid;

  // Opened by an error, it stays open once the error is fixed: otherwise the
  // keystroke that makes a field valid is the one that snaps the card shut
  // under the user's cursor.
  useEffect(() => {
    if (invalid || attention) {
      setOpen(true);
    }
  }, [invalid, attention]);

  return (
    <section className="rounded-card bg-navy-800 border border-navy-500 overflow-hidden">
      <header className="px-5 py-3.5 flex items-center justify-between gap-3 border-b border-navy-600">
        <div className="flex items-baseline gap-2.5 min-w-0">
          <h2 className="m-0 font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
            {title}
          </h2>
          {isBusy ? (
            <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300">
              reading…
            </span>
          ) : badge ? (
            <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-state-ok">
              {badge}
            </span>
          ) : null}
        </div>
        {/* Not offered while the card is forced open by an error: the press
            would do nothing, and a button that does nothing is worse than no
            button. */}
        <DsButton
          variant="subtle"
          size="md"
          onClick={() => setOpen((o) => !o)}
          isDisabled={invalid}
          className="flex-none"
        >
          {isOpen ? 'Done' : 'Edit'}
        </DsButton>
      </header>

      {isOpen ? (
        <div className="px-5 py-5 flex flex-col gap-5">{children}</div>
      ) : (
        <div className="px-5 py-1">{rows}</div>
      )}
    </section>
  );
};

export default CreateContainerForm;
